import { useEffect, useMemo, useRef, useState } from "react";

import useBodyScrollLock from "./useBodyScrollLock";
import useFocusTrap from "./useFocusTrap";
import { getVideoEmbed } from "./videoEmbed";

export default function useLandingVideoModal(defaultVideoUrl) {
  const [isOpen, setIsOpen] = useState(false);
  const [startAt, setStartAt] = useState(0);
  const [urlOverride, setUrlOverride] = useState(null);
  const playerRef = useRef(null);
  const modalRef = useRef(null);
  const videoUrl = urlOverride || defaultVideoUrl || null;
  const hasVideo = Boolean(videoUrl);
  const embed = useMemo(
    () => (isOpen ? getVideoEmbed(videoUrl, startAt) : null),
    [isOpen, videoUrl, startAt]
  );

  useBodyScrollLock(isOpen);
  useFocusTrap(isOpen, modalRef);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setUrlOverride(null);
        setStartAt(0);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const openModal = (seconds = 0, url) => {
    const target = url || defaultVideoUrl;
    if (!target) return;

    setUrlOverride(url || null);
    setStartAt(Number.isFinite(seconds) && seconds > 0 ? seconds : 0);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setUrlOverride(null);
    setStartAt(0);
  };

  const seekTo = (seconds) => {
    if (seconds == null) return;

    const node = playerRef.current;
    if (!node) return;

    if (embed?.provider === "file") {
      try {
        node.currentTime = seconds;
        const played = node.play();
        if (played?.catch) played.catch(() => {});
      } catch (error) {
        /* ignore seek errors */
      }
      return;
    }

    const win = node.contentWindow;
    if (!win) return;

    if (embed?.provider === "youtube") {
      win.postMessage(
        JSON.stringify({ event: "command", func: "seekTo", args: [seconds, true] }),
        "https://www.youtube.com"
      );
      win.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }),
        "https://www.youtube.com"
      );
    } else if (embed?.provider === "vimeo") {
      win.postMessage(
        JSON.stringify({ method: "setCurrentTime", value: seconds }),
        "https://player.vimeo.com"
      );
      win.postMessage(
        JSON.stringify({ method: "play" }),
        "https://player.vimeo.com"
      );
    }
  };

  return {
    isOpen,
    embed,
    playerRef,
    modalRef,
    hasVideo,
    videoUrl,
    openModal,
    closeModal,
    seekTo,
  };
}
